import { ReactElement } from "react";
import { useState } from "react";
import AsyncSelect from "react-select/async";

import { Entity } from "../../api";
import { useEntityCache } from "../../hooks/useEntityCache";
import {
  getBaseSelectStylesCompact,
  singleSelectStyles,
} from "../../styles/react-select";
import { Button } from "../catalyst/Button";
import { IconRenderer } from "../icons/IconRenderer";

const EntityPopupPicker = ({
  left,
  onClose,
  onSelect,
  top,
}: {
  left: number;
  onClose: () => void;
  onSelect: (entity: Entity) => void;
  top: number;
}): ReactElement => {
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);

  const { isLoading, loadOptions, selectEntity } = useEntityCache();
  return (
    <div
      className="fixed z-[9999] min-w-[240px] rounded-md border border-white/10 bg-transparent p-1.5 text-white shadow-[0_10px_24px_rgba(0,0,0,0.35)] dark:bg-zinc-900/75"
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
      style={{
        left,
        top,
      }}
    >
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[13px] font-bold">Select entity</div>
        <Button
          aria-label="Close"
          className="h-4 w-4 border-0 bg-transparent p-0 text-gray-400 hover:text-gray-300"
          onClick={onClose}
          plain
          size="xs"
        >
          ×
        </Button>
      </div>
      <AsyncSelect<Entity, false>
        cacheOptions
        className="w-full text-white"
        defaultOptions
        formatOptionLabel={(entity) => (
          <div className="flex items-center gap-1.5">
            <IconRenderer className="h-4 w-4" icon={entity.icon} />
            <span className="text-xs">{entity.name}</span>
          </div>
        )}
        getOptionLabel={(entity) => entity.name}
        getOptionValue={(entity) => entity.id}
        isLoading={isLoading}
        loadOptions={loadOptions}
        menuPortalTarget={document.body}
        noOptionsMessage={({ inputValue }) =>
          inputValue ? "No entities found" : "Loading..."
        }
        onChange={(selected) => {
          if (selected) {
            selectEntity(selected);
          }
          onSelect(selected as Entity);
          setSelectedEntity(selected);
        }}
        placeholder="Select an entity"
        styles={{
          ...getBaseSelectStylesCompact<Entity, false>(),
          ...singleSelectStyles,
        }}
        value={selectedEntity}
      />
    </div>
  );
};

export default EntityPopupPicker;
